import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Trash2, FileText, UserCheck, UserPlus, 
  RefreshCw, X, Edit, ChevronUp, HelpCircle, Calendar as CalendarIcon, Check,
  Eye, Paperclip, Download, ExternalLink, Upload, Phone, Mail, User, Loader2
} from 'lucide-react';
import StatusDropdown from '../components/StatusDropdown';
import PatientBusyAlert from '../components/PatientBusyAlert';
import PatientDetail from '../components/PatientDetail';
import Modal from '../components/Modal';
import Spinner from '../components/Spinner';
import { useWaitingRoom } from '../hooks/useWaitingRoom';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import './WaitingRoom.css';

const getLocalYYYYMMDD = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getInitials = (name) => {
  if (!name) return '-';
  const clean = name.trim();
  if (clean.toUpperCase().startsWith('ADMIN TURNO')) {
    const num = clean.replace(/[^0-9]/g, '');
    return num ? `AT${num.padStart(2, '0')}` : 'AT';
  }
  if (/^[A-Z][a-z]+[A-Z][a-z]*$/.test(clean)) {
    const caps = clean.match(/[A-Z]/g);
    if (caps && caps.length >= 2) return caps.join('');
  }
  const parts = clean.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 3).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const WaitingRoom = () => {
  const navigate = useNavigate();
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';
  const staffName = isAdmin ? '' : (activeStaff.full_name || '');

  const {
    viewDate, setViewDate,
    isReduced, setIsReduced,
    loading,
    professionals,
    selectedProfessional, setSelectedProfessional,
    searchTerm, setSearchTerm,
    isAdmitModalOpen, setIsAdmitModalOpen,
    admitData, setAdmitData,
    settings, setSettings,
    consultorios,
    attendedPatient, setAttendedPatient,
    filteredPatients,
    handleManualRefresh,
    updateStatus,
    handleAssignConsultory,
    handleBulkAssignConsultory,
    handleAttendPatient,
    handleCall,
    handleQuickAdmit,
    deleteAppointment,
    sortBy, setSortBy
  } = useWaitingRoom(staffName);

  const { addNotification } = useNotifications();
  const [selectedPatientInfo, setSelectedPatientInfo] = useState(null);
  const [selectedFullPatient, setSelectedFullPatient] = useState(null);
  const [modalFiles, setModalFiles] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [bulkConsultorio, setBulkConsultorio] = useState('');

  const openPatientInfoModal = async (app) => {
    setSelectedPatientInfo(app);
    let files = [];
    
    if (app.study_details?.attached_files && Array.isArray(app.study_details.attached_files)) {
      files = [...files, ...app.study_details.attached_files];
    }
    if (app.files && Array.isArray(app.files)) {
      files = [...files, ...app.files];
    }

    if (app.patient_id) {
      try {
        const { data: patData } = await supabase.from('patients').select('*').eq('id', app.patient_id).single();
        if (patData) {
          if (patData.attached_files && Array.isArray(patData.attached_files)) {
            patData.attached_files.forEach(f => {
              if (!files.some(existing => existing.url === f.url || existing.name === f.name)) {
                files.push(f);
              }
            });
          }
          setSelectedPatientInfo(prev => ({
            ...prev,
            patient_dni: prev.patient_dni || patData.dni,
            patient_phone: prev.patient_phone || patData.phone,
            patient_email: prev.patient_email || patData.email,
            insurance_provider: prev.study_details?.insurance_provider || patData.insurance_provider,
            insurance_plan: prev.study_details?.insurance_plan || patData.insurance_plan,
            birthdate: patData.birthdate,
            medical_notes: patData.medical_notes,
            full_patient_data: patData
          }));
        }
      } catch (e) {
        console.error(e);
      }
    }
    setModalFiles(files);
  };

  const handleUploadNewFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedPatientInfo) return;

    setIsUploadingFile(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage.from('patient-docs').upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('patient-docs').getPublicUrl(fileName);
      
      const newFileObj = { name: file.name, url: publicUrl, uploaded_at: new Date().toISOString() };
      const updatedFiles = [...modalFiles, newFileObj];
      setModalFiles(updatedFiles);

      const currentStudyDetails = selectedPatientInfo.study_details || {};
      const updatedStudyDetails = {
        ...currentStudyDetails,
        attached_files: [...(currentStudyDetails.attached_files || []), newFileObj]
      };

      await supabase.from('appointments').update({ study_details: updatedStudyDetails }).eq('id', selectedPatientInfo.id);
      
      if (selectedPatientInfo.patient_id) {
        const { data: pat } = await supabase.from('patients').select('attached_files').eq('id', selectedPatientInfo.patient_id).single();
        const patFiles = pat?.attached_files || [];
        await supabase.from('patients').update({ attached_files: [...patFiles, newFileObj] }).eq('id', selectedPatientInfo.patient_id);
      }

      addNotification({ title: 'Éxito', message: 'Archivo adjuntado correctamente al turno.', type: 'success' });
      handleManualRefresh();
    } catch (err) {
      console.error(err);
      addNotification({ title: 'Error', message: 'No se pudo subir el archivo: ' + err.message, type: 'error' });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleDeleteFile = async (indexToDelete) => {
    if (!selectedPatientInfo) return;
    if (!window.confirm('¿Está seguro de que desea eliminar este archivo adjunto?')) return;

    try {
      const updatedFiles = modalFiles.filter((_, idx) => idx !== indexToDelete);
      setModalFiles(updatedFiles);

      const currentStudyDetails = selectedPatientInfo.study_details || {};
      const updatedStudyDetails = {
        ...currentStudyDetails,
        attached_files: updatedFiles
      };

      await supabase.from('appointments').update({ study_details: updatedStudyDetails }).eq('id', selectedPatientInfo.id);

      if (selectedPatientInfo.patient_id) {
        await supabase.from('patients').update({ attached_files: updatedFiles }).eq('id', selectedPatientInfo.patient_id);
      }

      addNotification({ title: 'Éxito', message: 'Archivo eliminado correctamente.', type: 'success' });
      handleManualRefresh();
    } catch (err) {
      console.error(err);
      addNotification({ title: 'Error', message: 'No se pudo eliminar el archivo: ' + err.message, type: 'error' });
    }
  };

  const openFullPatientDetail = async (app) => {
    if (app.full_patient_data) {
      setSelectedFullPatient(app.full_patient_data);
      return;
    }
    if (app.patient_id) {
      const { data: pat } = await supabase.from('patients').select('*').eq('id', app.patient_id).single();
      if (pat) {
        setSelectedFullPatient(pat);
        return;
      }
    }
    const normDni = app.patient_dni || app.study_details?.patient_dni;
    if (normDni) {
      const { data: existing } = await supabase.from('patients').select('*').eq('dni', normDni).limit(1);
      if (existing && existing.length > 0) {
        setSelectedFullPatient(existing[0]);
        return;
      }
    }
    setSelectedFullPatient({
      id: app.patient_id || null,
      full_name: app.patient_name,
      dni: normDni || '',
      phone: app.patient_phone || '',
      email: app.patient_email || '',
      insurance_provider: app.study_details?.insurance_provider || 'PARTICULAR',
      insurance_plan: app.study_details?.insurance_plan || '-',
      medical_notes: app.admin_notes || app.notes || ''
    });
  };

  const getProfessionalTheme = (name) => {
    const themes = {
      'TODOS': { color: '#00e5ff', initials: '🌍' },
      'CONSULTORIOS EXTERNOS': { color: '#7e57c2', initials: 'CE' },
      'ECOGRAFIAS .': { color: '#ffee58', initials: 'E' },
      'FONOAUDIOLOGA .': { color: '#ffa726', initials: 'F' },
      'NEUMONOLOGO .': { color: '#fdd835', initials: 'N' },
      'NEUROLOGO .': { color: '#d4e157', initials: 'N' },
      'OTORRINO .': { color: '#66bb6a', initials: 'O' },
      'PSICOLOGIA .': { color: '#d4e157', initials: 'P' },
      'RAYOS X': { color: '#26c6da', initials: 'RX' }
    };
    return themes[name] || { color: '#00e5ff', initials: name?.substring(0, 2).toUpperCase() || 'P' };
  };

  const getAttachedFiles = (p) => {
    if (!p) return [];
    let details = p.study_details;
    if (typeof details === 'string') {
      try { details = JSON.parse(details); } catch (e) { details = {}; }
    }
    let files = [];
    if (details && Array.isArray(details.attached_files)) {
      files = [...files, ...details.attached_files];
    }
    if (Array.isArray(p.files)) {
      files = [...files, ...p.files];
    }
    return files;
  };

  const formatViewDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
    const todayStr = getLocalYYYYMMDD();
    const isToday = dateStr === todayStr;
    return `${dateObj.toLocaleDateString('es-ES', { day: 'numeric', month: 'numeric', year: 'numeric' })}${isToday ? ' (Hoy)' : ''}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) + ' hs';
    } catch {
      return '-';
    }
  };
  const onCallPatient = async (p) => {
    if (!p.consultorio_name && consultorios && consultorios.length > 0) {
      const match = consultorios.find(c => 
        c.name.toLowerCase().includes(p.professional_name?.toLowerCase()) || 
        p.professional_name?.toLowerCase().includes(c.name.toLowerCase())
      );
      if (match) {
        await handleAssignConsultory(p.id, match.name);
        p.consultorio_name = match.name;
      }
    }
    handleCall(p);
  };

  const getWaitTime = (p) => {
    if (!p || !p.arrival_time) return '-';
    try {
      const start = new Date(p.arrival_time);
      if (isNaN(start.getTime())) return '-';
      let end = new Date();
      
      if (p.status === 'atendido' && p.attended_time) {
        const attDate = new Date(p.attended_time);
        if (!isNaN(attDate.getTime())) end = attDate;
      } else if ((p.status === 'en-consultorio' || p.status === 'atendido') && p.updated_at) {
        const updDate = new Date(p.updated_at);
        if (!isNaN(updDate.getTime())) end = updDate;
      }
      
      const diffMs = end - start;
      if (isNaN(diffMs) || diffMs < 0) return '0 min';
      const diffMins = Math.floor(diffMs / 60000);
      return `${diffMins} min`;
    } catch {
      return '-';
    }
  };

  const renderAdmitModal = () => {
    return (
      <Modal
        isOpen={isAdmitModalOpen}
        onClose={() => { setIsAdmitModalOpen(false); setAdmitData({ patient_name: '', patient_dni: '', professional_name: staffName || '' }); }}
        title={admitData.id ? 'MODIFICAR ADMISIÓN' : 'NUEVA ADMISIÓN ESPONTÁNEA'}
        icon={UserPlus}
        iconColor="cyan"
        footer={
          <>
            <button className="btn-cancel-modal" onClick={() => { setIsAdmitModalOpen(false); setAdmitData({ patient_name: '', patient_dni: '', professional_name: staffName || '' }); }}>CANCELAR</button>
            <button className="btn-save-appointment" onClick={handleQuickAdmit}>{admitData.id ? 'GUARDAR CAMBIOS' : 'ADMITIR AHORA'}</button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-group full">
            <label>Nombre del Paciente</label>
            <input 
              type="text" 
              value={admitData.patient_name}
              onChange={(e) => setAdmitData({...admitData, patient_name: e.target.value})}
              placeholder="Ej: Juan Pérez"
            />
          </div>
          <div className="form-group">
            <label>DNI</label>
            <input 
              type="text" 
              value={admitData.patient_dni}
              onChange={(e) => setAdmitData({...admitData, patient_dni: e.target.value})}
              placeholder="Ej: 12345678"
            />
          </div>
          <div className="form-group">
            <label>Profesional / Especialidad</label>
            {staffName ? (
              <input 
                type="text" 
                readOnly 
                value={staffName} 
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', color: '#cbd5e1' }}
              />
            ) : (
              <select 
                value={admitData.professional_name}
                onChange={(e) => setAdmitData({...admitData, professional_name: e.target.value})}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
              >
                <option value="">Seleccione profesional...</option>
                {professionals.map(prof => (
                  <option key={prof.id} value={prof.name}>{prof.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  if (!selectedProfessional) {
    return (
      <div className="waiting-room-page">
        <div className="page-header">
          <div className="title-section">
            <h1>SALA DE ESPERA VIRTUAL</h1>
            <p>Seleccione una agenda para gestionar los pacientes en espera</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn-primary" 
              onClick={() => {
                setAdmitData({ patient_name: '', patient_dni: '', professional_name: staffName || '' });
                setIsAdmitModalOpen(true);
              }}
            >
              <UserPlus size={18} /> ADMISIÓN ESPONTÁNEA
            </button>
            <button className="btn-secondary" onClick={handleManualRefresh}>
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} /> ACTUALIZAR
            </button>
          </div>
        </div>

        <div className="professional-grid-wr">
          {/* General ununified view card */}
          <div className="prof-card-wr glass-panel-premium" onClick={() => setSelectedProfessional('TODOS')} style={{ border: '1px dashed rgba(0, 229, 255, 0.4)' }}>
            <div className="prof-card-icon" style={{ backgroundColor: '#00e5ff' }}>
              🌍
            </div>
            <div className="prof-card-info">
              <h3>TODAS LAS AGENDAS</h3>
              <span className="prof-specialty">Vista General Unificada</span>
            </div>
            <button className="btn-enter-room" style={{ borderColor: 'rgba(0, 229, 255, 0.4)', color: '#00e5ff' }}>VER SALA GENERAL</button>
          </div>

          {professionals.map(prof => (
            <div key={prof.id} className="prof-card-wr glass-panel-premium" onClick={() => setSelectedProfessional(prof.name)}>
              <div className="prof-card-icon" style={{ backgroundColor: getProfessionalTheme(prof.name).color }}>
                {getProfessionalTheme(prof.name).initials}
              </div>
              <div className="prof-card-info">
                <h3>{prof.name}</h3>
                <span className="prof-specialty">Agenda de Turnos</span>
              </div>
              <button className="btn-enter-room">VER SALA DE ESPERA VIRTUAL</button>
            </div>
          ))}
        </div>
        {renderAdmitModal()}
      </div>
    );
  }

  return (
    <div className="waiting-room-page">
      <div className="room-header-premium glass-panel compact">
        <div className="room-header-row-top">
          <div className="room-info-top">
            <div className="prof-badge-sm" style={{ backgroundColor: getProfessionalTheme(selectedProfessional).color }}>
              {getProfessionalTheme(selectedProfessional).initials}
            </div>
            <div className="room-title-text">
              <h2>
                Sala de Espera: 
                {!staffName ? (
                  <select
                    className="prof-switch-select"
                    value={selectedProfessional}
                    onChange={(e) => {
                      setSelectedProfessional(e.target.value);
                      setSearchTerm('');
                    }}
                  >
                    <option value="TODOS">TODAS LAS AGENDAS (General)</option>
                    {professionals.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="highlight-blue" style={{ marginLeft: '0.5rem' }}>{selectedProfessional}</span>
                )}
              </h2>
              <p>Viendo Fecha: <span className="highlight-date">{formatViewDate(viewDate)}</span></p>
            </div>
          </div>

          <div className="toggle-controls-group">
            <div className="toggle-item">
              <input type="checkbox" id="sound" checked={settings.playSound} onChange={(e) => setSettings({...settings, playSound: e.target.checked})} />
              <label htmlFor="sound">Reproducir Sonido</label>
            </div>
            <div className="toggle-item">
              <input type="checkbox" id="caller" checked={settings.useCaller} onChange={(e) => setSettings({...settings, useCaller: e.target.checked})} />
              <label htmlFor="caller">Utilizar Llamador</label>
            </div>
            <HelpCircle size={18} className="help-icon-wr" />
          </div>
        </div>

        <div className="room-controls-bar">
          <div className="search-bar-wr">
            <input
              type="text"
              placeholder="Buscar paciente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-wr"
            />
          </div>
          <div className="action-buttons-group">
            <button className="btn-wr-header" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={handleManualRefresh}>
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              <span>ACTUALIZAR</span>
            </button>
            <div className="btn-wr-header blue" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
              <CalendarIcon size={13} />
              <span>CAMBIAR FECHA</span>
              <input
                type="date"
                value={viewDate}
                onChange={(e) => setViewDate(e.target.value)}
                onClick={(e) => {
                  try {
                    if (typeof e.target.showPicker === 'function') {
                      e.target.showPicker();
                    }
                  } catch (err) {}
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  zIndex: 2
                }}
              />
            </div>
            {!staffName && (
              <button className="btn-wr-header gray" onClick={() => setSelectedProfessional(null)}>OTRO PROFESIONAL</button>
            )}
            <button className="btn-wr-header green" onClick={() => setIsReduced(!isReduced)}>
              {isReduced ? 'LISTADO COMPLETO' : 'LISTADO REDUCIDO'}
            </button>
            <button 
              className="btn-wr-header cyan" 
              onClick={() => {
                const url = selectedProfessional && selectedProfessional !== 'TODOS'
                  ? `#/patients-caller?professional=${encodeURIComponent(selectedProfessional)}`
                  : '#/patients-caller';
                window.open(url, '_blank');
              }}
            >
              🖥️ PANTALLA LLAMADOR
            </button>
          </div>
        </div>
      </div>

      <div className="wr-main-content card glass-panel-premium" style={{ maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', position: 'relative', padding: 0 }}>
        {/* Bulk Consultory Assignment Control */}
        <div className="wr-bulk-assign-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--primary-cyan)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              🏢 Asignación Masiva:
            </span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              ({selectedProfessional && selectedProfessional !== 'TODOS' ? `Filtro: ${selectedProfessional}` : 'Todos los turnos'})
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <select
              value={bulkConsultorio}
              onChange={(e) => setBulkConsultorio(e.target.value)}
              style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(0, 229, 255, 0.3)',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '0.78rem',
                outline: 'none',
                maxWidth: '190px'
              }}
            >
              <option value="">Seleccionar Consultorio...</option>
              {consultorios.map((c, idx) => (
                <option key={idx} value={c.name}>{c.name}</option>
              ))}
            </select>

            <button
              className="btn-wr-header cyan"
              style={{ padding: '4px 10px', fontSize: '0.72rem', whiteSpace: 'nowrap' }}
              onClick={() => {
                const targetIds = filteredPatients.map(p => p.id);
                handleBulkAssignConsultory(bulkConsultorio, targetIds);
              }}
            >
              <Check size={14} />
              ASIGNAR A TODOS ({filteredPatients.length})
            </button>
          </div>
        </div>

        <table className="wr-table-premium">
          <thead>
            <tr>
              <th onClick={() => setSortBy('time')} style={{cursor: 'pointer'}} title="Ordenar por Hora de Turno">
                {sortBy === 'time' ? <ChevronUp size={14} /> : <span style={{display: 'inline-block', width: '14px'}}></span>} Hora Turno
              </th>
              <th>Paciente</th>
              {!isReduced && <th>Tipo Turno</th>}
              <th>Consultorio</th>
              <th>Estado</th>
              {!isReduced && <th onClick={() => setSortBy('arrival')} style={{cursor: 'pointer'}} title="Ordenar por Hora de Llegada">
                {sortBy === 'arrival' ? <ChevronUp size={14} /> : <span style={{display: 'inline-block', width: '14px'}}></span>} Hora Llegada
              </th>}
              {!isReduced && <th>Min. Espera</th>}
              {!isReduced && <th>Atendido</th>}
              {!isReduced && <th>Evoluc.</th>}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isReduced ? 5 : 10} className="text-center-p"><Spinner text="Cargando sala..." /></td></tr>
            ) : filteredPatients.length === 0 ? (
              <tr>
                <td colSpan={isReduced ? 5 : 10} className="empty-room-msg">
                  El Profesional {selectedProfessional} no tiene turnos el día: {formatViewDate(viewDate)}
                </td>
              </tr>
            ) : (
              filteredPatients.map((p) => {
                const attachedList = getAttachedFiles(p);
                return (
                  <tr key={p.id} className={`wr-row status-${p.status}`}>
                    <td className="time-cell-wr">{p.appointment_time?.substring(0, 5)} hs</td>
                    <td>
                      <div className="patient-cell-wr">
                        <span 
                          className="p-name" 
                          onClick={() => openPatientInfoModal(p)} 
                          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }} 
                          title="Ver datos y archivos adjuntos del paciente"
                        >
                          {p.patient_name}
                          <Eye size={13} style={{ opacity: 0.6, color: '#00e5ff' }} />
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                          <span className="p-dni">DNI: {p.patient_dni || p.study_details?.patient_dni || '---'}</span>
                          {attachedList.length > 0 && (
                            <span 
                              onClick={() => openPatientInfoModal(p)}
                              style={{ background: 'rgba(0, 229, 255, 0.15)', border: '1px solid #00e5ff', color: '#00e5ff', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px', fontWeight: 600 }}
                              title="Tiene archivos adjuntos"
                            >
                              <Paperclip size={10} /> {attachedList.length}
                            </span>
                          )}
                        </div>
                        {(p.admin_notes || p.notes || (isReduced && p.study_type && p.study_type !== 'CONSULTA GENERAL')) && (
                          <div style={{ fontSize: '0.75rem', color: '#ffb020', marginTop: '4px', lineHeight: '1.2', maxWidth: '200px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {isReduced && p.study_type && p.study_type !== 'CONSULTA GENERAL' && (
                              <div style={{color: '#00e5ff', marginBottom: '2px'}}>{p.study_type}</div>
                            )}
                            {(p.admin_notes || p.notes) && (
                              <div><strong style={{ opacity: 0.8 }}>Obs:</strong> {p.admin_notes || p.notes}</div>
                            )}
                          </div>
                        )}
                        <PatientBusyAlert appointment={p} />
                      </div>
                    </td>
                    {!isReduced && <td className="type-cell-wr">{p.study_type || 'CONSULTA'}</td>}
                    <td>
                      <select 
                        className="consultory-select-wr"
                        value={p.consultorio_name || ''}
                        onChange={(e) => handleAssignConsultory(p.id, e.target.value)}
                      >
                        <option value="">Seleccionar...</option>
                        {consultorios.map((c, idx) => (
                          <option key={idx} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td><StatusDropdown value={p.status} onChange={(newStatus) => updateStatus(p.id, newStatus, p.patient_name)} /></td>
                    {!isReduced && <td className="text-center">{formatTime(p.arrival_time)}</td>}
                    {!isReduced && <td className="text-center">{getWaitTime(p)}</td>}
                    {!isReduced && <td className="text-center">{p.status === 'atendido' ? formatTime(p.attended_time || p.updated_at) : '-'}</td>}
                    {!isReduced && (
                      <td className="text-center">
                        <FileText 
                          size={16} 
                          className="icon-btn-wr" 
                          title="Ver Ficha y Adjuntos del Paciente" 
                          onClick={() => openPatientInfoModal(p)} 
                        />
                      </td>
                    )}
                    <td>
                      <div className="wr-actions-flex">
                        {p.status === 'pendiente' && (
                          <button className="btn-wr-action admit" onClick={() => updateStatus(p.id, 'presente', p.patient_name)}>
                            <UserCheck size={14} /> ADMITIR
                          </button>
                        )}
                        {(p.status === 'presente' || p.status === 'en-llamado') && (
                          <button className="btn-wr-action call" onClick={() => onCallPatient(p)}>
                            <Bell size={14} /> {p.status === 'en-llamado' ? 'RE-LLAMAR' : 'LLAMAR'}
                          </button>
                        )}
                        {p.status === 'en-llamado' && (
                          <button className="btn-wr-action enter" onClick={() => updateStatus(p.id, 'en-consultorio', p.patient_name)}>
                            🚪 INGRESÓ
                          </button>
                        )}
                        {p.status === 'en-consultorio' && (
                          <button className="btn-wr-action attend" onClick={() => handleAttendPatient(p)}>
                            ✓ ATENDIDO
                          </button>
                        )}
                        <div className="wr-more-actions">
                          <button 
                            className="btn-icon-sm"
                            title="Ver datos del paciente y archivos adjuntos"
                            onClick={() => openPatientInfoModal(p)}
                            style={{ color: '#00e5ff', background: 'rgba(0, 229, 255, 0.12)', border: '1px solid rgba(0, 229, 255, 0.3)' }}
                          >
                            <Eye size={14} />
                          </button>
                          <button 
                            className="btn-icon-sm"
                            title="Editar datos del turno"
                            onClick={() => {
                              setAdmitData({
                                id: p.id,
                                patient_name: p.patient_name,
                                patient_dni: p.patient_dni || '',
                                professional_name: p.professional_name || ''
                              });
                              setIsAdmitModalOpen(true);
                            }}
                          >
                            <Edit size={14} />
                          </button>
                          <button className="btn-icon-sm delete" onClick={() => deleteAppointment(p.id)}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {renderAdmitModal()}

      {/* Attended patient modal */}
      <Modal
        isOpen={!!attendedPatient}
        onClose={() => setAttendedPatient(null)}
        title="CONSULTA FINALIZADA"
        icon={Check}
        theme="premium"
        footer={
          <>
            <button 
              className="btn-cancel-modal" 
              onClick={() => setAttendedPatient(null)}
              style={{ minWidth: '100px' }}
            >
              CERRAR
            </button>
            <button 
              className="btn-save-appointment" 
              onClick={() => {
                if (attendedPatient?.patient_id) {
                  navigate(`/patients/${attendedPatient.patient_id}`);
                } else {
                  setAttendedPatient(null);
                }
              }}
              style={{ padding: '0 1.5rem' }}
            >
              IR A EVOLUCIONES
            </button>
          </>
        }
      >
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.25rem', fontWeight: '800', marginBottom: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {attendedPatient?.patient_name}
          </div>
          <p style={{ color: 'white', fontSize: '1.15rem', fontWeight: '600', marginBottom: '0.75rem', lineHeight: '1.4' }}>
            Este Paciente ya ha sido marcado como "Consulta Finalizada"
          </p>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem' }}>
            Puede seguir ingresando a sus Evoluciones.
          </p>
        </div>
      </Modal>

      {/* Patient Info & Attached Files Modal */}
      {selectedPatientInfo && (
        <Modal
          isOpen={!!selectedPatientInfo}
          onClose={() => setSelectedPatientInfo(null)}
          title="INFORMACIÓN Y ADJUNTOS DEL PACIENTE"
          icon={User}
          theme="premium"
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: '10px' }}>
              <button 
                className="btn-wr-header blue" 
                onClick={() => {
                  const targetApp = selectedPatientInfo;
                  setSelectedPatientInfo(null);
                  openFullPatientDetail(targetApp);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FileText size={14} />
                VER HISTORIA CLÍNICA (EVOLUCIONES)
              </button>
              <button className="btn-cancel-modal" onClick={() => setSelectedPatientInfo(null)}>
                CERRAR
              </button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', color: 'var(--text-primary)' }}>
            {/* Patient Header Card */}
            <div className="wr-patient-header-card" style={{ background: 'rgba(0, 229, 255, 0.06)', borderLeft: '4px solid #00e5ff', padding: '14px 18px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 className="wr-patient-name" style={{ margin: 0, fontSize: '1.2rem', color: '#fff', fontWeight: 600 }}>{selectedPatientInfo.patient_name}</h3>
                <span style={{ fontSize: '0.85rem', color: '#00e5ff' }}>DNI: {selectedPatientInfo.patient_dni || selectedPatientInfo.study_details?.patient_dni || 'Sin DNI'}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#ffd54f' }}>{selectedPatientInfo.study_type || 'CONSULTA GENERAL'}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{selectedPatientInfo.appointment_time?.substring(0, 5)} hs | {selectedPatientInfo.professional_name}</div>
              </div>
            </div>

            {/* Info Grid */}
            <div className="wr-patient-info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', background: 'rgba(255,255,255,0.02)', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Teléfono</span>
                <strong style={{ fontSize: '0.9rem' }}>{selectedPatientInfo.patient_phone || selectedPatientInfo.phone || 'No registrado'}</strong>
                {selectedPatientInfo.patient_phone && (
                  <a 
                    href={`https://wa.me/${selectedPatientInfo.patient_phone.replace(/[^0-9]/g, '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', color: '#25D366', fontSize: '0.75rem', textDecoration: 'none' }}
                  >
                    <Phone size={12} /> WhatsApp
                  </a>
                )}
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Email</span>
                <strong style={{ fontSize: '0.9rem' }}>{selectedPatientInfo.patient_email || selectedPatientInfo.email || 'No registrado'}</strong>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Obra Social / Prepaga</span>
                <strong style={{ fontSize: '0.9rem' }}>{selectedPatientInfo.insurance_provider || selectedPatientInfo.study_details?.insurance_provider || 'PARTICULAR'}</strong>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Otorgado por</span>
                <strong style={{ fontSize: '0.9rem', color: '#00e5ff' }} title={selectedPatientInfo.created_by_user || selectedPatientInfo.study_details?.created_by_user || 'Sistema'}>
                  {(() => {
                    const creator = selectedPatientInfo.created_by_user || selectedPatientInfo.study_details?.created_by_user || selectedPatientInfo.requester_name;
                    if (!creator) return '-';
                    const initials = getInitials(creator);
                    return `${initials} (${creator})`;
                  })()}
                </strong>
              </div>

              {selectedPatientInfo.birthdate && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Fecha de Nacimiento</span>
                  <strong style={{ fontSize: '0.9rem' }}>{selectedPatientInfo.birthdate}</strong>
                </div>
              )}
            </div>

            {/* Observations */}
            {(selectedPatientInfo.admin_notes || selectedPatientInfo.notes || selectedPatientInfo.medical_notes) && (
              <div style={{ background: 'rgba(255, 171, 0, 0.08)', border: '1px solid rgba(255, 171, 0, 0.25)', padding: '12px 14px', borderRadius: '8px' }}>
                <span style={{ fontSize: '0.75rem', color: '#ffb300', fontWeight: 600, display: 'block', marginBottom: '4px' }}>OBSERVACIONES Y NOTAS</span>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#ffe082', whiteSpace: 'pre-wrap' }}>
                  {selectedPatientInfo.admin_notes || selectedPatientInfo.notes || selectedPatientInfo.medical_notes}
                </p>
              </div>
            )}

            {/* Attached Files Section */}
            <div className="wr-attached-section" style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary-cyan)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Paperclip size={16} /> ARCHIVOS ADJUNTOS Y ÓRDENES MÉDICAS ({modalFiles.length})
                </span>
                <label className="btn-wr-header green" style={{ padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  {isUploadingFile ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  <span>+ SUBIR ADJUNTO</span>
                  <input type="file" onChange={handleUploadNewFile} style={{ display: 'none' }} disabled={isUploadingFile} />
                </label>
              </div>

              {modalFiles.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {modalFiles.map((file, idx) => (
                    <div 
                      key={idx} 
                      className="wr-file-item-card"
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                        <FileText size={18} style={{ color: '#00e5ff', flexShrink: 0 }} />
                        <span className="wr-file-title" style={{ fontSize: '0.85rem', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>
                          {file.name || `Archivo adjunto #${idx + 1}`}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <a 
                          href={file.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ background: 'rgba(0, 229, 255, 0.15)', border: '1px solid #00e5ff', color: '#00e5ff', padding: '5px 12px', borderRadius: '5px', fontSize: '0.75rem', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <ExternalLink size={12} /> ABRIR / DESCARGAR
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteFile(idx)}
                          title="Eliminar este archivo adjunto"
                          style={{ background: 'rgba(255, 82, 82, 0.15)', border: '1px solid #ff5252', color: '#ff5252', padding: '5px 8px', borderRadius: '5px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No hay archivos adjuntos asociados a este turno o paciente.
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Full Patient Detail Modal */}
      {selectedFullPatient && (
        <PatientDetail 
          patient={selectedFullPatient} 
          onClose={() => setSelectedFullPatient(null)} 
        />
      )}
    </div>
  );
};

export default WaitingRoom;
